// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Info,
  Close,
  Edit,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  profitabilityAPI,
  contractsAPI,
  ClientProfitabilitySummary,
  ContractProfitability,
  MonthlyProfitability,
} from '../services/api';
import { getPanamaDateString, getDaysAgo } from '../utils/dateUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function ClientProfitability() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientProfitabilitySummary[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [periodStart, setPeriodStart] = useState(() => getDaysAgo(30));
  const [periodEnd, setPeriodEnd] = useState(() => getPanamaDateString());

  // New filter state
  const [sortBy, setSortBy] = useState<'revenue' | 'profit' | 'real_profit' | 'margin'>('revenue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [contractType, setContractType] = useState<'leasing' | 'service' | 'combined' | ''>('');

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Totals
  const [grandTotalRevenue, setGrandTotalRevenue] = useState(0);
  const [grandTotalProjectedProfit, setGrandTotalProjectedProfit] = useState(0);
  const [grandTotalRealProfit, setGrandTotalRealProfit] = useState(0);

  // Contract detail dialog
  const [selectedClient, setSelectedClient] = useState<ClientProfitabilitySummary | null>(null);
  const [contracts, setContracts] = useState<ContractProfitability[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ContractProfitability | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyProfitability[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Edit leasing cost dialog
  const [editingContract, setEditingContract] = useState<ContractProfitability | null>(null);
  const [editDownPayment, setEditDownPayment] = useState<string>('');
  const [editFinancedAmount, setEditFinancedAmount] = useState<string>('');
  const [editFinanceInterest, setEditFinanceInterest] = useState<string>('');
  const [editTermMonths, setEditTermMonths] = useState<number>(36);
  const [editSaving, setEditSaving] = useState(false);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, periodStart, periodEnd, sortBy, sortOrder, contractType]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page === 0) {
        loadData();
      } else {
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(debounce);
  }, [search]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await profitabilityAPI.getClientsSummary({
        period_start: periodStart,
        period_end: periodEnd,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        contract_type: contractType || undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });

      setClients(response.clients);
      setTotalCount(response.total_count);
      setGrandTotalRevenue(response.grand_total_revenue);
      setGrandTotalProjectedProfit(response.grand_total_projected_profit);
      setGrandTotalRealProfit(response.grand_total_real_profit);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar datos de rentabilidad');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientContracts = async (client: ClientProfitabilitySummary) => {
    setSelectedClient(client);
    setContractsLoading(true);
    setTabValue(0);

    try {
      const response = await profitabilityAPI.getContractsProfitability({
        client_id: client.client_id,
        period_start: periodStart,
        period_end: periodEnd,
      });
      setContracts(response.contracts);
    } catch (err: any) {
      console.error('Error loading contracts:', err);
    } finally {
      setContractsLoading(false);
    }
  };

  const loadContractMonthly = async (contract: ContractProfitability) => {
    setSelectedContract(contract);
    setMonthlyLoading(true);
    setTabValue(1);

    try {
      const response = await profitabilityAPI.getContractMonthly(contract.contract_id, {
        period_start: periodStart,
        period_end: periodEnd,
      });
      setMonthlyData(response.monthly_data);
    } catch (err: any) {
      console.error('Error loading monthly data:', err);
    } finally {
      setMonthlyLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setSelectedClient(null);
    setContracts([]);
    setSelectedContract(null);
    setMonthlyData([]);
  };

  const handleOpenEditDialog = async (contract: ContractProfitability, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingContract(contract);
    setEditSuccess(null);

    // Try to get current leasing breakdown from the contract
    try {
      const contractDetails = await contractsAPI.getDetails(contract.contract_id);
      setEditDownPayment(String(contractDetails.leasing_down_payment || ''));
      setEditFinancedAmount(String(contractDetails.leasing_financed_amount || ''));
      setEditFinanceInterest(String(contractDetails.leasing_finance_interest || ''));
      setEditTermMonths(contractDetails.term_months || 36);
    } catch (err) {
      // If can't fetch, start with empty
      setEditDownPayment('');
      setEditFinancedAmount('');
      setEditFinanceInterest('');
      setEditTermMonths(36);
    }
  };

  const handleCloseEditDialog = () => {
    setEditingContract(null);
    setEditDownPayment('');
    setEditFinancedAmount('');
    setEditFinanceInterest('');
    setEditSuccess(null);
  };

  // Calculate monthly leasing cost based on breakdown
  const calculateMonthlyLeasingCost = (): number => {
    const downPayment = parseFloat(editDownPayment) || 0;
    const financed = parseFloat(editFinancedAmount) || 0;
    const interest = parseFloat(editFinanceInterest) || 0;
    const total = downPayment + financed + interest;
    return total > 0 ? total / editTermMonths : 0;
  };

  const handleSaveLeaseCost = async () => {
    if (!editingContract) return;

    setEditSaving(true);
    try {
      const downPayment = parseFloat(editDownPayment) || 0;
      const financed = parseFloat(editFinancedAmount) || 0;
      const interest = parseFloat(editFinanceInterest) || 0;

      await contractsAPI.update(editingContract.contract_id, {
        leasing_down_payment: downPayment,
        leasing_financed_amount: financed,
        leasing_finance_interest: interest,
      });

      setEditSuccess('Costo de leasing actualizado correctamente');

      // Reload contracts to reflect the change
      if (selectedClient) {
        const response = await profitabilityAPI.getContractsProfitability({
          client_id: selectedClient.client_id,
          period_start: periodStart,
          period_end: periodEnd,
        });
        setContracts(response.contracts);
      }

      // Close dialog after a brief delay
      setTimeout(() => {
        handleCloseEditDialog();
      }, 1500);
    } catch (err: any) {
      console.error('Error saving lease cost:', err);
      setError(err.response?.data?.detail || 'Error al guardar costo de leasing');
    } finally {
      setEditSaving(false);
    }
  };

  const formatCurrency = (amount: number | string): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const formatPercent = (value: number | string): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${(num || 0).toFixed(1)}%`;
  };

  const formatNumber = (num: number | string): string => {
    const value = typeof num === 'string' ? parseFloat(num) : num;
    return new Intl.NumberFormat('es-PA').format(value || 0);
  };

  const getMarginColor = (margin: number | string) => {
    const num = typeof margin === 'string' ? parseFloat(margin) : margin;
    if (num >= 30) return 'success';
    if (num >= 15) return 'warning';
    return 'error';
  };

  const getVarianceColor = (variance: number | string) => {
    const num = typeof variance === 'string' ? parseFloat(variance) : variance;
    if (num > 0) return 'success.main';
    if (num < 0) return 'error.main';
    return 'text.secondary';
  };

  if (loading && clients.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Rentabilidad de Clientes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Ingresos Totales
              </Typography>
              <Typography variant="h4">
                {formatCurrency(grandTotalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Ganancia Proyectada
              </Typography>
              <Typography variant="h4" color={parseFloat(String(grandTotalProjectedProfit)) >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(grandTotalProjectedProfit)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Ganancia Real
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" color={parseFloat(String(grandTotalRealProfit)) >= 0 ? 'success.main' : 'error.main'}>
                  {formatCurrency(grandTotalRealProfit)}
                </Typography>
                {parseFloat(String(grandTotalRealProfit)) > parseFloat(String(grandTotalProjectedProfit)) ? (
                  <TrendingUp color="success" />
                ) : parseFloat(String(grandTotalRealProfit)) < parseFloat(String(grandTotalProjectedProfit)) ? (
                  <TrendingDown color="error" />
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar cliente"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha inicio"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha fin"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={`${sortBy}-${sortOrder}`}
                  label="Ordenar por"
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                >
                  <MenuItem value="revenue-desc">Mayor Ingreso</MenuItem>
                  <MenuItem value="revenue-asc">Menor Ingreso</MenuItem>
                  <MenuItem value="real_profit-desc">Mayor Ganancia</MenuItem>
                  <MenuItem value="real_profit-asc">Menor Ganancia</MenuItem>
                  <MenuItem value="margin-desc">Mayor Margen</MenuItem>
                  <MenuItem value="margin-asc">Menor Margen</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Contrato</InputLabel>
                <Select
                  value={contractType}
                  label="Tipo Contrato"
                  onChange={(e) => setContractType(e.target.value as typeof contractType)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="leasing">Solo Leasing</MenuItem>
                  <MenuItem value="service">Solo Servicio</MenuItem>
                  <MenuItem value="combined">Combinados</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button variant="outlined" onClick={loadData} fullWidth>
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Paper>
        <Box display="flex" justifyContent="flex-end" p={1}>
          <Tooltip title="Pantalla completa">
            <IconButton onClick={() => setIsFullscreen(true)}>
              <Fullscreen />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="center">Contratos</TableCell>
                <TableCell align="center">Equipos</TableCell>
                <TableCell align="right">Ingresos</TableCell>
                <TableCell align="right">
                  <Tooltip title="Costo real basado en despachos de consumibles y visitas de servicio">
                    <span>Costo Real</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Ganancia Real</TableCell>
                <TableCell align="right">
                  <Tooltip title="Costo proyectado basado en estimaciones de consumibles y depreciacion">
                    <span>Costo Proy.</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Ganancia Proy.</TableCell>
                <TableCell align="center">Margen Proy.</TableCell>
                <TableCell align="center">Margen Real</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No se encontraron clientes con contratos activos
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client.client_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => loadClientContracts(client)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {client.client_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {client.client_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{client.contracts_count}</TableCell>
                    <TableCell align="center">{client.equipment_count}</TableCell>
                    <TableCell align="right">{formatCurrency(client.total_revenue)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        color={parseFloat(String(client.total_real_cost)) > parseFloat(String(client.total_projected_cost)) ? 'error.main' : 'success.main'}
                      >
                        {formatCurrency(client.total_real_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Typography
                          color={parseFloat(String(client.total_real_profit)) >= parseFloat(String(client.total_projected_profit)) ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(client.total_real_profit)}
                        </Typography>
                        {parseFloat(String(client.total_profit_variance)) > 0 ? (
                          <TrendingUp fontSize="small" color="success" />
                        ) : parseFloat(String(client.total_profit_variance)) < 0 ? (
                          <TrendingDown fontSize="small" color="error" />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(client.total_projected_cost)}</TableCell>
                    <TableCell align="right">{formatCurrency(client.total_projected_profit)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatPercent(client.avg_projected_margin)}
                        size="small"
                        color={getMarginColor(client.avg_projected_margin)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatPercent(client.avg_real_margin)}
                        size="small"
                        color={getMarginColor(client.avg_real_margin)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); loadClientContracts(client); }}>
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por pagina:"
        />
      </Paper>

      {/* Fullscreen Table Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        fullScreen
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Rentabilidad de Clientes</Typography>
            <IconButton onClick={() => setIsFullscreen(false)}>
              <FullscreenExit />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="center">Contratos</TableCell>
                  <TableCell align="center">Equipos</TableCell>
                  <TableCell align="right">Ingresos</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Costo real basado en despachos de consumibles y visitas de servicio">
                      <span>Costo Real</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Ganancia Real</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Costo proyectado basado en estimaciones de consumibles y depreciacion">
                      <span>Costo Proy.</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Ganancia Proy.</TableCell>
                  <TableCell align="center">Margen Proy.</TableCell>
                  <TableCell align="center">Margen Real</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No se encontraron clientes con contratos activos
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow
                      key={client.client_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { setIsFullscreen(false); loadClientContracts(client); }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {client.client_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {client.client_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{client.contracts_count}</TableCell>
                      <TableCell align="center">{client.equipment_count}</TableCell>
                      <TableCell align="right">{formatCurrency(client.total_revenue)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={parseFloat(String(client.total_real_cost)) > parseFloat(String(client.total_projected_cost)) ? 'error.main' : 'success.main'}
                        >
                          {formatCurrency(client.total_real_cost)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <Typography
                            color={parseFloat(String(client.total_real_profit)) >= parseFloat(String(client.total_projected_profit)) ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(client.total_real_profit)}
                          </Typography>
                          {parseFloat(String(client.total_profit_variance)) > 0 ? (
                            <TrendingUp fontSize="small" color="success" />
                          ) : parseFloat(String(client.total_profit_variance)) < 0 ? (
                            <TrendingDown fontSize="small" color="error" />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(client.total_projected_cost)}</TableCell>
                      <TableCell align="right">{formatCurrency(client.total_projected_profit)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatPercent(client.avg_projected_margin)}
                          size="small"
                          color={getMarginColor(client.avg_projected_margin)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatPercent(client.avg_real_margin)}
                          size="small"
                          color={getMarginColor(client.avg_real_margin)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalles">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsFullscreen(false); loadClientContracts(client); }}>
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por pagina:"
          />
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog
        open={!!selectedClient}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Rentabilidad: {selectedClient?.client_name}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Contratos" />
            <Tab label="Tendencia Mensual" disabled={!selectedContract} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {contractsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contrato</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="center">Equipos</TableCell>
                      <TableCell align="right">Ingresos</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Costo que paga la empresa al banco/financiera por el leasing del equipo">
                          <span>Costo Leasing</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">Costo Total</TableCell>
                      <TableCell align="right">Ganancia Real</TableCell>
                      <TableCell align="center">Margen</TableCell>
                      <TableCell align="center">Copias B/N</TableCell>
                      <TableCell align="center">Copias Color</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contracts.map((contract) => (
                      <TableRow
                        key={contract.contract_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => loadContractMonthly(contract)}
                      >
                        <TableCell>{contract.contract_number}</TableCell>
                        <TableCell>
                          <Chip
                            label={contract.contract_type}
                            size="small"
                            variant="outlined"
                            color={contract.contract_type === 'leasing' || contract.contract_type === 'combined' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">{contract.equipment_count}</TableCell>
                        <TableCell align="right">{formatCurrency(contract.revenue.total_revenue)}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            <Typography
                              color={contract.real_costs.leasing_cost > 0 ? 'text.primary' : 'text.secondary'}
                              fontStyle={contract.real_costs.leasing_cost > 0 ? 'normal' : 'italic'}
                            >
                              {contract.real_costs.leasing_cost > 0
                                ? formatCurrency(contract.real_costs.leasing_cost)
                                : 'Sin definir'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={parseFloat(String(contract.real_costs.total_cost)) > parseFloat(String(contract.projected_costs.total_cost)) ? 'error.main' : 'inherit'}
                          >
                            {formatCurrency(contract.real_costs.total_cost)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color={getVarianceColor(contract.real_profit)}>
                            {formatCurrency(contract.real_profit)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatPercent(contract.real_margin_pct)}
                            size="small"
                            color={getMarginColor(contract.real_margin_pct)}
                          />
                        </TableCell>
                        <TableCell align="center">{formatNumber(contract.total_bw_copies)}</TableCell>
                        <TableCell align="center">{formatNumber(contract.total_color_copies)}</TableCell>
                        <TableCell align="center">
                          {(contract.contract_type === 'leasing' || contract.contract_type === 'combined') && (
                            <Tooltip title="Editar costo de leasing">
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenEditDialog(contract, e)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {monthlyLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : selectedContract ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Contrato: {selectedContract.contract_number}
                </Typography>

                <Grid container spacing={3}>
                  {/* Revenue & Profit Chart */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Ingresos vs Ganancias Mensuales
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip
                          formatter={(value: number) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="revenue" name="Ingresos" fill="#2196f3" />
                        <Bar dataKey="projected_profit" name="Ganancia Proy." fill="#ff9800" />
                        <Bar dataKey="real_profit" name="Ganancia Real" fill="#4caf50" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>

                  {/* Costs Comparison Chart */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Comparacion de Costos
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip
                          formatter={(value: number) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="projected_cost"
                          name="Costo Proyectado"
                          stroke="#ff9800"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="real_cost"
                          name="Costo Real"
                          stroke="#f44336"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>

                  {/* Usage Chart */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Uso Mensual
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip
                          formatter={(value: number) => formatNumber(value)}
                        />
                        <Legend />
                        <Bar dataKey="bw_copies" name="Copias B/N" fill="#607d8b" />
                        <Bar dataKey="color_copies" name="Copias Color" fill="#9c27b0" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>

                {/* Monthly Data Table */}
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Datos Mensuales
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Mes</TableCell>
                          <TableCell align="right">Ingresos</TableCell>
                          <TableCell align="right">Costo Proy.</TableCell>
                          <TableCell align="right">Costo Real</TableCell>
                          <TableCell align="right">Ganancia Proy.</TableCell>
                          <TableCell align="right">Ganancia Real</TableCell>
                          <TableCell align="center">Copias B/N</TableCell>
                          <TableCell align="center">Copias Color</TableCell>
                          <TableCell align="center">Visitas</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthlyData.map((month) => (
                          <TableRow key={month.month}>
                            <TableCell>{month.month}</TableCell>
                            <TableCell align="right">{formatCurrency(month.revenue)}</TableCell>
                            <TableCell align="right">{formatCurrency(month.projected_cost)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                color={parseFloat(String(month.real_cost)) > parseFloat(String(month.projected_cost)) ? 'error.main' : 'inherit'}
                              >
                                {formatCurrency(month.real_cost)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(month.projected_profit)}</TableCell>
                            <TableCell align="right">
                              <Typography color={getVarianceColor(month.real_profit)}>
                                {formatCurrency(month.real_profit)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">{formatNumber(month.bw_copies)}</TableCell>
                            <TableCell align="center">{formatNumber(month.color_copies)}</TableCell>
                            <TableCell align="center">{month.service_visits}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Seleccione un contrato para ver la tendencia mensual
              </Alert>
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Leasing Cost Dialog */}
      <Dialog
        open={!!editingContract}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Editar Costo de Leasing
        </DialogTitle>
        <DialogContent>
          {editSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {editSuccess}
            </Alert>
          )}

          {editingContract && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Contrato: <strong>{editingContract.contract_number}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Cliente: <strong>{editingContract.client_name}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Tipo: <Chip label={editingContract.contract_type} size="small" />
              </Typography>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Ingresos por Leasing (lo que paga el cliente):
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(editingContract.revenue.lease_revenue)}
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Costo de Adquisicion del Equipo:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pago Inicial"
                    value={editDownPayment}
                    onChange={(e) => setEditDownPayment(e.target.value)}
                    helperText="Down payment"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Monto Financiado"
                    value={editFinancedAmount}
                    onChange={(e) => setEditFinancedAmount(e.target.value)}
                    helperText="Principal del prestamo"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Intereses Totales"
                    value={editFinanceInterest}
                    onChange={(e) => setEditFinanceInterest(e.target.value)}
                    helperText="Intereses del prestamo"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Resumen del Costo:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">
                      Total Equipo:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency((parseFloat(editDownPayment) || 0) + (parseFloat(editFinancedAmount) || 0) + (parseFloat(editFinanceInterest) || 0))}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">
                      Plazo:
                    </Typography>
                    <Typography variant="h6">
                      {editTermMonths} meses
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">
                      Costo Mensual:
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(calculateMonthlyLeasingCost())}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Ejemplo:</strong> Si la empresa pago $4,000 de inicial, financio $16,000 con $3,000 de intereses por 36 meses,
                  el costo mensual seria ${((4000 + 16000 + 3000) / 36).toFixed(2)}.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={editSaving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveLeaseCost}
            disabled={editSaving || (!editDownPayment && !editFinancedAmount)}
          >
            {editSaving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
